import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Component, Input, ViewEncapsulation, Output, EventEmitter, OnChanges } from "@angular/core";
import { ActivatedRoute } from '@angular/router';
import { DateTime as LuxonDateTime } from 'luxon';

import { Communication } from 'src/providers/communication/communication';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Client } from 'src/providers/client/client';
import { Genome } from 'src/providers/genome/genome';
import { Schedule } from 'src/providers/schedule/schedule';
import { ModalController } from '@ionic/angular';
import { Util } from 'src/shared/util/util';

import { Loading } from 'src/shared/components/loading/loading';
import { RightSidePanelService } from "src/shared/providers/providers/right-side-panel";
import { PastPrivateJobPage } from "src/pages/more/past-private-job/past-private-job";
import { PastJobPage } from "src/pages/more/past-job/past-job";
import { JobPage } from "src/pages/schedule/job/job";
import { BookService } from 'src/shared/services/book.service';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

@Component({
    selector: 'message',
    templateUrl: 'message.component.html',
    styleUrls: ['message.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class MessageComponent implements OnChanges {

  errorMessage: string;
  @Input() addExtraPadding = true;
  @Input() last = false;
  @Input() message: any;
  @Input() showJobLink = true;
  @Input() isInvoiceNote: boolean;
  @Input() proId: any;
  @Input() isUnknownNumber = false;
  @Output() messageImagesLoaded = new EventEmitter<void>();

  messageDateTime: string;
  private loadedPhotoNotes = 0;
  private totalPhotoNotes = 0;

  constructor(
    private client: Client,
    public communication: Communication,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    private genome: Genome,
    public route: ActivatedRoute,
    public schedule: Schedule,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private util: Util,
    private bookService: BookService
  ) {}

  ngOnChanges() {
    if (this.message) {
      this.totalPhotoNotes = 0;
      if (this.message.content?.files?.length) {
        this.totalPhotoNotes += 1;
      }
      if (this.message.media_url) {
        this.totalPhotoNotes += 1;
      }
      this.loadedPhotoNotes = 0;
      // Pre-calculate messageDateTime for better performance
      this.calculateMessageDateTime();
    }
  }

  calculateMessageDateTime(): void {
    if (!this.message) {
      this.messageDateTime = '';
      return;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const relativeDate = LuxonDateTime.fromISO(this.message.sent_at || this.message.data?.created_at).setZone(timezone).toRelative();
    if (relativeDate.includes('day') || relativeDate.includes('month') || relativeDate.includes('year')) {
      const dateInTimezone = LuxonDateTime.fromISO(this.message.sent_at || this.message.data?.created_at).setZone(timezone).toISO();
      this.messageDateTime = `${new ParseDateTimePipe().transform(dateInTimezone, 'ddd MMM d')  } ${  new ParseDateTimePipe().transform(dateInTimezone, 'h:mma')}`;
    } else {
      this.messageDateTime = relativeDate;
    }
  }

  onPhotoNoteLoaded() {
    this.loadedPhotoNotes++;
    if (this.loadedPhotoNotes === this.totalPhotoNotes) {
      this.messageImagesLoaded.emit();
    }
  }

  getJobDate(date, timezone) {
    const dateInTimezone = LuxonDateTime.fromISO(date).setZone(timezone).toISO();
    return new ParseDateTimePipe().transform(dateInTimezone, 'MMM d');
  }

  @Loading('', true)
  async goToJobPage(jobMetadata: {
    id?: number | null,
    state?: string | null,
    address_id?: number | null,
    is_private?: boolean | null,
    timezone_name?: string | null,
    start_datetime?: string | null,
    [key: string]: any;
  }) {
    try {
      const jobIsScheduled = jobMetadata?.state === 'scheduled';
      const isRightSide = await this.storage.retrieve('dialog-right-side-open');
      const currentPath = this.route.snapshot.url.join('/');
      const params = {
        jobId: jobMetadata?.id,
        addressId: jobMetadata?.address_id,
        backPage: currentPath,
        proId: this.proId
      }
      if (isRightSide) {
        this.storage.save('dialog-params', params);
        const isPastJob = jobIsScheduled ? false : true;
        this.navigateToJobPageInsideDialog(isPastJob, jobMetadata?.is_private);
        return;
      }
      localStorage.setItem('pastJobBackPage', currentPath);
      localStorage.setItem('jobBackPage', currentPath);
      let path = `job/${jobMetadata?.address_id}/${jobMetadata?.id}`;
      if (!jobIsScheduled && jobMetadata?.is_private) {
        path = `past-private-job/${jobMetadata?.id}`
      } else if (!jobIsScheduled && !jobMetadata?.is_private) {
        path = `past-job/${jobMetadata?.id}`;
      }
      this.navCtrl.navigateForward(path, params);
      const modal = await this.modalCtrl.getTop();
      if (modal) {
        modal.dismiss();
      }
    } catch (err) {
      this.errorMessage =  err.error ? err.error.message : err.message;
    }
  }

  navigateToJobPageInsideDialog(isPastJob: boolean, isPrivateJob: boolean): void {
    if (isPastJob && isPrivateJob) {
      this.rightSidePanelService.replaceComponentInDialog(PastPrivateJobPage);
    } else if (isPastJob) {
      this.rightSidePanelService.replaceComponentInDialog(PastJobPage);
    } else {
      this.rightSidePanelService.replaceComponentInDialog(JobPage);
    }
  }

  get internalNoteStyleObject() {
    return {
      'padding-top' : '5px',
      'padding-bottom' : '5px'
    };
  }

  internalNoteStyle(isInternalNote) {
    if (isInternalNote) {
      return {
        'background-color': '#F0F0F0',
        'padding-top' : '5px',
        'padding-bottom' : '5px'
      };
    }
  }

  errorSendingStyle(isError) {
    if (isError) {
      return {
        'background-color': 'rgba(255, 108, 120, 0.1)',
        'padding-top' : '5px',
        'padding-bottom' : '5px'
      };
    }
  }

  getMessageStyles(message) {
    return {
      ...this.internalNoteStyleObject,
      ...this.errorSendingStyle(message?.content?.phone_status === 'failed')
    };
  }

  async cancelJob() {
    const params: ConfirmPageParamsModel = {
      title: 'Cancel Job?',
      body: 'Are you sure you want to cancel this job?', //Add job details
      backText: 'Go Back',
      confirmText: 'Cancel',
      confirmAction: await this.confirmCancelJob.bind(this),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmCancelJob() {
    try {
      const payload = {
        user_data: {
          booking_id: this.message?.content?.chat_suggestion?.data?.booking_id
        }
      }
      await this.communication.performAISuggestion(payload, this.message?.content?.chat_suggestion?.id);
      const successMessage = 'Job Cancelled';
      this.util.showSuccess(successMessage);
      this.modalCtrl.dismiss();
      this.message.content.chat_suggestion.status = 'performed';
      this.rightSidePanelService.closeRightSidePanel();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async rescheduleJob() {
    const params: ConfirmPageParamsModel = {
      title: 'Reschedule Job?',
      body: 'Are you sure you want to reschedule this job?', //Add job details
      backText: 'Go Back',
      confirmText: 'Reschedule',
      confirmAction: await this.confirmRescheduleJob.bind(this),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmRescheduleJob() {
    try {
      const payload = {
        user_data: {
          booking_id: this.message?.content?.chat_suggestion?.data?.booking_id,
          new_date: this.message?.content?.chat_suggestion?.data?.new_date,
          new_start_time: this.message?.content?.chat_suggestion?.data?.new_start_time
        }
      }
      await this.communication.performAISuggestion(payload, this.message?.content?.chat_suggestion?.id);
      const successMessage = 'Job Rescheduled';
      this.util.showSuccess(successMessage);
      this.modalCtrl.dismiss();
      this.message.content.chat_suggestion.status = 'performed';
      this.rightSidePanelService.closeRightSidePanel();
    } catch(err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async goToBookingViewRescheduleJob() {
    const address = await this.genome.getAddress(this.message.metadata.address.id);
    const params = {
      address,
      bookingType: 'reschedule_job',
      jobId: this.message.metadata.job.id,
      bookingId: this.message.metadata.booking.id,
      hkId: this.proId,
      filterByPro: true
    };
    await this.storage.save('bookJobBackPage', 'messages');
    await this.bookService.goToBookJob(params);
  }

  async dismissSuggestion() {
    await this.communication.dismissAISuggestion(this.message?.content?.chat_suggestion?.id);
    const successMessage = 'Suggestion Hidden';
    this.util.showSuccess(successMessage);
    this.message.content.chat_suggestion.status = '';
  }

  getSuggestionJobTime(time) {
    return LuxonDateTime.fromFormat(time, "HH:mm").toFormat("h:mma").toLowerCase();
  }

  getSuggestionJobDate(date) {
    return new ParseDateTimePipe().transform(date, 'MMM d');
  }

}
